/**
 * 订单核对 Tab — Phase 1 占位页 + Mock 数据预览
 *
 * 设计文档: docs/08_user_workflow.md §⑥
 * 此功能 Phase 1 不实现后端，仅展示 UI 占位和 Mock 数据让用户感知功能形态。
 */
import React from "react";
import { Table, Tag, Space, Typography, Alert, Card, Row, Col, Statistic } from "antd";
import { AuditOutlined, LinkOutlined, CheckCircleOutlined, QuestionCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";

const { Text } = Typography;

// ---- Mock 数据 ----
const MOCK_ORDERS = [
    {
        id: "1",
        order_number: "PO-2026-00471",
        file_name: "FM_Order_Mar_001.pdf",
        status: "matched",
        matched_invoices: ["INV-2026-0891"],
        total_amount: 12450.0,
        currency: "USD",
        match_confidence: 0.95,
    },
    {
        id: "2",
        order_number: "PO-2026-00472",
        file_name: "FM_Order_Mar_002.pdf",
        status: "partial",
        matched_invoices: ["INV-2026-0892"],
        total_amount: 8320.0,
        currency: "USD",
        match_confidence: 0.72,
    },
    {
        id: "3",
        order_number: "PO-2026-00473",
        file_name: "FM_Order_Mar_003.pdf",
        status: "unmatched",
        matched_invoices: [],
        total_amount: 5100.0,
        currency: "USD",
        match_confidence: 0,
    },
    {
        id: "4",
        order_number: "PO-2026-00474",
        file_name: "FM_Order_Mar_004.pdf",
        status: "matched",
        matched_invoices: ["INV-2026-0893", "INV-2026-0894"],
        total_amount: 23800.0,
        currency: "USD",
        match_confidence: 0.98,
    },
];

const STATUS_CONFIG = {
    matched: { color: "success", label: "已匹配", icon: <CheckCircleOutlined /> },
    partial: { color: "warning", label: "部分匹配", icon: <QuestionCircleOutlined /> },
    unmatched: { color: "error", label: "未匹配", icon: <CloseCircleOutlined /> },
};

const columns = [
    {
        title: "订单号",
        dataIndex: "order_number",
        key: "order_number",
        width: 160,
        render: (text) => <Text strong>{text}</Text>,
    },
    {
        title: "文件名",
        dataIndex: "file_name",
        key: "file_name",
        width: 200,
        ellipsis: true,
    },
    {
        title: "金额",
        dataIndex: "total_amount",
        key: "amount",
        width: 120,
        align: "right",
        render: (v, record) => `${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 })} ${record.currency}`,
    },
    {
        title: "匹配状态",
        dataIndex: "status",
        key: "status",
        width: 110,
        render: (status) => {
            const cfg = STATUS_CONFIG[status] || {};
            return (
                <Tag color={cfg.color} icon={cfg.icon}>
                    {cfg.label || status}
                </Tag>
            );
        },
    },
    {
        title: "关联账单",
        dataIndex: "matched_invoices",
        key: "invoices",
        width: 180,
        render: (invoices) =>
            invoices?.length > 0 ? (
                <Space size={4}>
                    <LinkOutlined style={{ color: "#8c8c8c" }} />
                    <Text style={{ fontSize: 12 }}>{invoices.join(", ")}</Text>
                </Space>
            ) : (
                <Text type="secondary">—</Text>
            ),
    },
    {
        title: "匹配置信度",
        dataIndex: "match_confidence",
        key: "confidence",
        width: 100,
        align: "center",
        render: (v) => {
            if (!v) return <Text type="secondary">—</Text>;
            const percent = Math.round(v * 100);
            const color = percent >= 90 ? "#52c41a" : percent >= 70 ? "#faad14" : "#ff4d4f";
            return <Text style={{ color, fontWeight: 500 }}>{percent}%</Text>;
        },
    },
];

const OrderReconciliationTab = () => {
    const matchedCount = MOCK_ORDERS.filter((o) => o.status === "matched").length;
    const partialCount = MOCK_ORDERS.filter((o) => o.status === "partial").length;
    const unmatchedCount = MOCK_ORDERS.filter((o) => o.status === "unmatched").length;

    return (
        <div>
            <Alert message="功能预览" description="订单核对功能即将上线。以下为模拟数据预览，展示功能上线后的大致形态。用户将可以上传订单原始凭证（PDF），系统自动通过 AI 解析并与账单和航班数据进行三方交叉核对。" type="info" showIcon icon={<AuditOutlined />} style={{ marginBottom: 16 }} />

            {/* Mock 统计卡片 */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={8}>
                    <Card size="small" bodyStyle={{ textAlign: "center" }}>
                        <Statistic title="已匹配" value={matchedCount} valueStyle={{ color: "#52c41a" }} prefix={<CheckCircleOutlined />} />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card size="small" bodyStyle={{ textAlign: "center" }}>
                        <Statistic title="部分匹配" value={partialCount} valueStyle={{ color: "#faad14" }} prefix={<QuestionCircleOutlined />} />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card size="small" bodyStyle={{ textAlign: "center" }}>
                        <Statistic title="未匹配" value={unmatchedCount} valueStyle={{ color: "#ff4d4f" }} prefix={<CloseCircleOutlined />} />
                    </Card>
                </Col>
            </Row>

            {/* Mock 列表 */}
            <Table rowKey="id" columns={columns} dataSource={MOCK_ORDERS} pagination={false} size="small" />
        </div>
    );
};

export default OrderReconciliationTab;
